// src/pages/property-details/components/PropertyTabs.jsx
import React, { useRef } from 'react';
import Icon from '../../../components/AppIcon';

const PropertyTabs = ({ property, activeTab, onTabChange, currentUserId }) => {
  const mapRef = useRef(null);

  const tabs = [
    { id: 'description', label: 'Description', icon: 'FileText' },
    { id: 'offers', label: 'Offers', icon: 'Gavel' },
    { id: 'field-activities', label: 'Tarla Faaliyetleri', icon: 'Tractor' },
    { id: 'features', label: 'Features', icon: 'Star' },
    { id: 'location', label: 'Farm Location', icon: 'MapPin' }
  ];

  const renderDescription = () => (
    <div className="prose max-w-none">
      <div className="text-text-primary whitespace-pre-line leading-relaxed">
        {property?.description}
      </div>
      
      {property?.harvestDate && (
        <div className="mt-6 p-4 bg-success-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Calendar" size={16} className="text-success" />
            <h4 className="font-semibold text-success">Harvest Information</h4>
          </div>
          <p className="text-sm text-success-700">
            Harvested on: {new Date(property.harvestDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      )}

    </div>
  );

  const renderOffers = () => (
    <div>
      {property?.currentBids && property.currentBids.length > 0 ? (
        <div>
          <h3 className="text-lg font-semibold text-text-primary mb-4">Current Bids</h3>
          <div className="space-y-3">
            {[...(property.currentBids || [])]
              .sort((a, b) => b.amount - a.amount)
              .map((bid, index) => (
                <div
                  key={bid.id}
                  className={`flex items-center justify-between py-3 px-4 rounded-lg border ${
                    bid?.buyerId && currentUserId && bid.buyerId === currentUserId
                      ? 'bg-primary-50 border-primary-200'
                      : 'bg-background border-border'
                  }`}
                >
                  <div>
                    <p className="font-medium text-text-primary">
                      {new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 2,
                      }).format(bid.amount)}
                    </p>
                    <p className="text-sm text-text-secondary">
                      {(bid.acceptedQuantity || bid.quantity)} {property.unit} • {new Date(bid.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Icon
                        name={
                          bid?.buyerId && currentUserId && bid.buyerId === currentUserId
                            ? 'UserCheck'
                            : 'UserRound'
                        }
                        size={15}
                        className={
                          bid?.buyerId && currentUserId && bid.buyerId === currentUserId
                            ? 'text-primary'
                            : 'text-text-secondary'
                        }
                      />
                      <p
                        className={`text-sm font-medium ${
                          bid?.buyerId && currentUserId && bid.buyerId === currentUserId
                            ? 'text-primary'
                            : 'text-text-primary'
                        }`}
                      >
                        {bid?.buyerId && currentUserId && bid.buyerId === currentUserId
                          ? 'Size Ait'
                          : bid.bidder}
                      </p>
                    </div>
                    {bid.status === 'ACCEPTED' ? (
                      <span className="inline-block px-2 py-1 bg-success text-white text-xs rounded-md mr-2">
                        Accepted
                      </span>
                    ) : null}
                    {index === 0 && (
                      <span className="inline-block px-2 py-1 bg-success text-white text-xs rounded-md">
                        Highest
                      </span>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <Icon name="Gavel" size={48} className="text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No bids yet for this product</p>
        </div>
      )}
    </div>
  );

  const renderFieldActivities = () => {
    const sourceSnapshot =
      property?.externalSource?.sourceSnapshot && typeof property.externalSource.sourceSnapshot === 'object'
        ? property.externalSource.sourceSnapshot
        : null;
    const activitySnapshots = Array.isArray(property?.externalSource?.activitiesSnapshot)
      ? property.externalSource.activitiesSnapshot
      : [];

    const formatDisplayDate = (value) => {
      if (!value) return 'Paylasilmadi';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return 'Paylasilmadi';
      return parsed.toLocaleDateString('tr-TR', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const resolveActivityIcon = (activity) => {
      const label = `${activity?.taskName || ''} ${activity?.activityType || ''}`.toLowerCase();
      if (label.includes('sulama') || label.includes('irrigation')) return 'Droplets';
      if (label.includes('hasat') || label.includes('harvest')) return 'Package';
      if (label.includes('ekim') || label.includes('plant')) return 'Sprout';
      if (label.includes('gubre') || label.includes('fertiliz')) return 'Leaf';
      if (label.includes('ilac') || label.includes('pest')) return 'Bug';
      if (label.includes('budama') || label.includes('prun')) return 'Scissors';
      return 'Tractor';
    };

    const resolveResourceIcon = (resource) => {
      const type = `${resource?.resourceType || ''}`.toLowerCase();
      const label = `${resource?.label || ''}`.toLowerCase();
      const combined = `${type} ${label}`;
      if (combined.includes('water') || combined.includes('su') || combined.includes('irrigation') || combined.includes('sulama')) return 'Droplets';
      if (combined.includes('fertiliz') || combined.includes('gubre')) return 'Leaf';
      if (combined.includes('pesticide') || combined.includes('herbicide') || combined.includes('ilac') || combined.includes('zirai')) return 'Bug';
      if (combined.includes('seed') || combined.includes('tohum') || combined.includes('sapling') || combined.includes('fide')) return 'Sprout';
      if (combined.includes('fuel') || combined.includes('yakıt') || combined.includes('yakit')) return 'Fuel';
      if (combined.includes('labor') || combined.includes('isci') || combined.includes('meal') || combined.includes('yemek')) return 'Users';
      if (combined.includes('equipment') || combined.includes('ekipman') || combined.includes('machinery')) return 'Wrench';
      if (combined.includes('electricity') || combined.includes('elektrik')) return 'Zap';
      if (combined.includes('transport') || combined.includes('nakliye')) return 'Truck';
      return 'Package2';
    };

    const formatResourceValue = (resource) => {
      if (resource?.quantityText) return resource.quantityText;
      if (typeof resource?.quantity === 'number' && Number.isFinite(resource.quantity)) {
        return `${resource.quantity}${resource?.unit ? ` ${resource.unit}` : ''}`;
      }
      return 'Detay paylasilmadi';
    };

    if (!property?.externalSource) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Tractor" size={28} className="text-text-secondary" />
          </div>
          <p className="font-medium text-text-primary mb-1">Tarla Faaliyeti Bulunamadi</p>
          <p className="text-sm text-text-secondary">Bu urun icin paylasilmis tarla faaliyeti bulunmuyor.</p>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        {/* Agrochain Kaynak Bilgisi */}
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 bg-primary-50 rounded-xl border border-primary-100">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
              <Icon name="Link" size={16} className="text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-primary">Agrochain Entegrasyonu</p>
              <p className="text-xs text-primary-700 mt-0.5">
                Faaliyetler ve kaynak kullanimi urun borsasina Agrochain uzerinden aktarildi.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-primary-700">
            {sourceSnapshot?.fieldName && (
              <div className="flex items-center gap-1.5">
                <Icon name="MapPin" size={13} className="text-primary" />
                <span>{sourceSnapshot.fieldName}</span>
              </div>
            )}
            {sourceSnapshot?.expectedHarvest && (
              <div className="flex items-center gap-1.5">
                <Icon name="Calendar" size={13} className="text-primary" />
                <span>{formatDisplayDate(sourceSnapshot.expectedHarvest)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Faaliyet Listesi */}
        {activitySnapshots.length > 0 ? (
          <div className="space-y-4">
            {activitySnapshots.map((activity, index) => {
              const hasResources = Array.isArray(activity?.sharedResources) && activity.sharedResources.length > 0;
              const usageResources = hasResources
                ? activity.sharedResources.filter((r) => r?.source === 'usage')
                : [];
              const requirementResources = hasResources
                ? activity.sharedResources.filter((r) => r?.source === 'requirement')
                : [];
              const displayResources = usageResources.length > 0 ? usageResources : requirementResources;
              const resourceSectionLabel = usageResources.length > 0 ? 'Gerceklesen Kaynak Kullanimi' : 'Planlanan Kaynak Gereksinimleri';

              return (
                <div
                  key={activity?.id || `activity-${index}`}
                  className="border border-border rounded-xl overflow-hidden bg-background"
                >
                  {/* Faaliyet Başlığı */}
                  <div className="flex flex-wrap items-start justify-between gap-3 p-4 md:p-5 border-b border-border bg-secondary-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <Icon name={resolveActivityIcon(activity)} size={18} className="text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-text-primary">
                          {activity?.taskName || activity?.activityType || 'Faaliyet'}
                        </h4>
                        {activity?.activityType && activity?.taskName !== activity?.activityType && (
                          <p className="text-xs text-text-secondary mt-0.5">{activity.activityType}</p>
                        )}
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-50 text-success text-xs font-medium">
                      <Icon name="CheckCircle" size={12} />
                      Tamamlandi
                    </span>
                  </div>

                  <div className="p-4 md:p-5 space-y-4">
                    {/* Meta Bilgiler */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="Calendar" size={13} className="text-text-secondary" />
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary">Tamamlanma Tarihi</p>
                          <p className="text-sm font-medium text-text-primary">{formatDisplayDate(activity?.completedAt)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="UserCheck" size={13} className="text-text-secondary" />
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary">Gerceklestiren</p>
                          <p className="text-sm font-medium text-text-primary">{activity?.completedBy || 'Paylasilmadi'}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name="Users" size={13} className="text-text-secondary" />
                        </div>
                        <div>
                          <p className="text-xs text-text-secondary">Atanan Personel</p>
                          <p className="text-sm font-medium text-text-primary">
                            {Array.isArray(activity?.assignedTo) && activity.assignedTo.length > 0
                              ? activity.assignedTo.join(', ')
                              : 'Paylasilmadi'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {activity?.description && (
                      <p className="text-sm text-text-primary leading-relaxed">{activity.description}</p>
                    )}

                    {/* Kaynak Kullanımı */}
                    {hasResources ? (
                      <div className="rounded-xl border border-border overflow-hidden">
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-secondary-50 border-b border-border">
                          <div className="flex items-center gap-2">
                            <Icon name="BarChart2" size={14} className="text-text-secondary" />
                            <h5 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                              {resourceSectionLabel}
                            </h5>
                          </div>
                          <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary">
                            {displayResources.length} kalem
                          </span>
                        </div>
                        <div className="divide-y divide-border">
                          {displayResources.map((resource, resourceIndex) => (
                            <div
                              key={resource?.key || `resource-${resourceIndex}`}
                              className="flex items-center justify-between gap-3 px-4 py-3"
                            >
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <Icon name={resolveResourceIcon(resource)} size={13} className="text-primary" />
                                </div>
                                <span className="text-sm text-text-primary truncate">
                                  {resource?.label || 'Kaynak'}
                                </span>
                              </div>
                              <span className="text-sm font-semibold text-text-primary whitespace-nowrap">
                                {formatResourceValue(resource)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-secondary-50 border border-border">
                        <Icon name="Info" size={14} className="text-text-secondary flex-shrink-0" />
                        <p className="text-xs text-text-secondary">Bu faaliyet icin kaynak bilgisi paylasilmadi.</p>
                      </div>
                    )}

                    {activity?.notes && (
                      <div className="rounded-xl bg-warning-50 border border-warning-100 px-4 py-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Icon name="StickyNote" size={13} className="text-warning-600" />
                          <p className="text-xs font-semibold uppercase tracking-wide text-warning-600">Notlar</p>
                        </div>
                        <p className="text-sm text-text-primary whitespace-pre-line">{activity.notes}</p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-10">
            <div className="w-14 h-14 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Icon name="FileText" size={24} className="text-text-secondary" />
            </div>
            <p className="font-medium text-text-primary mb-1">Faaliyet Paylasilmadi</p>
            <p className="text-sm text-text-secondary">Bu ilanda paylasilan tarla faaliyeti bulunmuyor.</p>
          </div>
        )}
      </div>
    );
  };

  const renderFeatures = () => (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {property?.features?.map((feature, index) => (
          <div key={index} className="flex items-center space-x-3 p-3 bg-background rounded-md">
            <Icon name="Check" size={16} className="text-success flex-shrink-0" />
            <span className="text-text-primary">{feature}</span>
          </div>
        ))}
      </div>
      
      {property?.organic && (
        <div className="mt-6 p-4 bg-success-50 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Leaf" size={16} className="text-success" />
            <h4 className="font-semibold text-success">Organic Certification</h4>
          </div>
          <p className="text-sm text-success-700">
            This product is certified organic and grown without synthetic pesticides, herbicides, or fertilizers.
          </p>
        </div>
      )}
      
      {(!property?.features || property.features.length === 0) && (
        <div className="text-center py-8">
          <Icon name="Star" size={48} className="text-secondary mx-auto mb-4" />
          <p className="text-text-secondary">No features listed for this product</p>
        </div>
      )}
    </div>
  );

  const renderLocation = () => (
    <div className="space-y-6">
      {/* Farm Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-background rounded-md">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="MapPin" size={16} className="text-primary" />
            <h4 className="font-semibold text-text-primary">Farm Location</h4>
          </div>
          <p className="text-text-secondary">{property?.address}</p>
        </div>
        
        <div className="p-4 bg-background rounded-md">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Truck" size={16} className="text-primary" />
            <h4 className="font-semibold text-text-primary">Delivery Available</h4>
          </div>
          <p className="text-text-secondary">Local delivery within 25 miles</p>
        </div>

        <div className="p-4 bg-background rounded-md">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Clock" size={16} className="text-primary" />
            <h4 className="font-semibold text-text-primary">Farm Hours</h4>
          </div>
          <p className="text-text-secondary">Mon-Sat: 7:00 AM - 6:00 PM</p>
        </div>

        <div className="p-4 bg-background rounded-md">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Phone" size={16} className="text-primary" />
            <h4 className="font-semibold text-text-primary">Contact</h4>
          </div>
          <p className="text-text-secondary">{property?.seller?.phone}</p>
        </div>
      </div>
      
      {/* Map Placeholder */}
      <div className="bg-secondary-100 rounded-lg h-64 md:h-80 flex items-center justify-center">
        <div className="text-center">
          <Icon name="Map" size={48} className="text-secondary mx-auto mb-2" />
          <p className="text-text-secondary">Interactive farm location map would be embedded here</p>
          <p className="text-sm text-text-secondary mt-1">
            Coordinates: {property?.coordinates?.lat}, {property?.coordinates?.lng}
          </p>
        </div>
      </div>
      
      {/* Nearby Services */}
      <div>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Nearby Agricultural Services</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            { name: 'Farm Supply Store', type: 'Equipment', distance: '2.5 miles' },
            { name: 'Farmers Market', type: 'Market', distance: '5.2 miles' },
            { name: 'Agricultural Extension Office', type: 'Services', distance: '8.1 miles' },
            { name: 'Organic Certification Office', type: 'Certification', distance: '12.3 miles' }
          ].map((place, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-background rounded-md">
              <div className="flex items-center space-x-3">
                <Icon 
                  name={
                    place.type === 'Equipment' ? 'Wrench' :
                    place.type === 'Market' ? 'ShoppingBag' :
                    place.type === 'Services' ? 'Building' :
                    place.type === 'Certification' ? 'Award' : 'MapPin'
                  } 
                  size={16} 
                  className="text-primary" 
                />
                <div>
                  <p className="font-medium text-text-primary">{place.name}</p>
                  <p className="text-sm text-text-secondary">{place.type}</p>
                </div>
              </div>
              <span className="text-sm font-medium text-primary">{place.distance}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'description':
        return renderDescription();
      case 'offers':
        return renderOffers();
      case 'field-activities':
        return renderFieldActivities();
      case 'features':
        return renderFeatures();
      case 'location':
        return renderLocation();
      default:
        return renderDescription();
    }
  };

  return (
    <div className="card overflow-hidden">
      {/* Tab Navigation */}
      <div className="border-b border-border">
        <div className="flex overflow-x-auto no-scrollbar">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'text-primary border-b-2 border-primary bg-primary-50' :'text-text-secondary hover:text-text-primary hover:bg-secondary-100'
              }`}
            >
              <Icon name={tab.icon} size={16} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default PropertyTabs;
